import * as React from 'react';

import WidgetCard from 'components/Widget/WidgetCard';
import TextEditor from 'ui/TextEditor/TextEditor';
import {
    WidgetContent,
    WidgetT,
} from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { parseMentionsFrom } from 'ui/Mention/helpers';
import { BUDGET, DECISION } from 'business/constants';
import PresentationModeContext from 'routes/Dashboard/components/PresentationModeContext';
import { useWarnUserIfNoAccessRights } from './helpers';
import { NestedMenuOptionT } from 'ui/NestedMenu/NestedMenu';
import { UserInfoWithIdT } from 'business/definitions';
import { WidgetParent } from 'components/Widget/WidgetFromType/constants';
import { CHART_ACTION_TYPE } from 'ui/NestedMenu/constants';

type Props = {
    editMode: boolean;
    canEdit: boolean;
    widget: WidgetT;
    widgetParent: WidgetParent.DASHBOARD | WidgetParent.ARGUMENT;
    menuItems: NestedMenuOptionT[];
    onEditWidget: (arg1: { widget: WidgetT; content: WidgetContent }) => void;
};

export default function ParagraphWidget({
    editMode,
    canEdit,
    widget,
    widgetParent,
    menuItems,
    onEditWidget,
}: Props): React.ReactElement {
    const [isEditable, setIsEditable] = React.useState(false);

    const warnUserIfNoAccessRights = useWarnUserIfNoAccessRights();

    const onSave = React.useCallback(
        (html: string, users: UserInfoWithIdT[]) => {
            setIsEditable(false);
            const oldContent = widget.content || {};

            if (widgetParent === WidgetParent.DASHBOARD) {
                const oldMentions = parseMentionsFrom(oldContent?.tiptap);
                const newMentions = parseMentionsFrom(html);

                const diffMentions = newMentions.filter(
                    (mention) => !oldMentions.includes(mention),
                );

                warnUserIfNoAccessRights(diffMentions, users);
            }

            onEditWidget({
                widget,
                content: { ...oldContent, tiptap: html },
            });
        },
        [JSON.stringify(widget), setIsEditable, warnUserIfNoAccessRights],
    );

    const MODULE_FROM_WIDGET = {
        [WidgetParent.DASHBOARD]: DECISION,
        [WidgetParent.ARGUMENT]: BUDGET,
    } as const;
    const module = MODULE_FROM_WIDGET[widgetParent];
    const editParagraph = {
        action: CHART_ACTION_TYPE.Update,
        menuLevel: 0,
        onClick: () => {
            setIsEditable(true);
        },
    };

    const [isInPresentationMode] = React.useContext(PresentationModeContext);

    const style: React.CSSProperties = {
        padding: '0.5rem 0.5rem 0',
        flexGrow: '1',
    };

    const ref = React.useRef<HTMLDivElement>(null);

    return (
        <WidgetCard
            editMode={editMode}
            canEdit={canEdit}
            menuItems={[editParagraph, ...menuItems]}
            onDoubleClick={() => {
                if (canEdit && !isInPresentationMode) {
                    setIsEditable(true);
                }
            }}
            disableRightClick={isEditable}
            data-testid="paragraph-widget"
        >
            <div ref={ref} style={{ display: 'flex', maxHeight: '100%' }}>
                <TextEditor
                    style={style}
                    canEdit={canEdit}
                    parentRef={ref}
                    isEditable={isEditable}
                    content={widget.content?.tiptap || ''}
                    onSave={onSave}
                    module={module}
                    isParagraphWidget
                    mentionBoundingBox={{ left: 0, right: 0 }}
                />
            </div>
        </WidgetCard>
    );
}
